import { faEllipsisVertical, faFilter } from '@fortawesome/pro-solid-svg-icons';
import { validateActiveFilters } from '@supply-mover-2-0/core';
import type { HTMLAttributes, ReactNode } from 'react';
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';
import { Icon } from '../../../components/base/Icon';
import { FilterGroup } from '../../../components/filters/FilterGroup';
import { Container } from '../../../components/layout/Container';
import { Dropdown } from '../../../components/overlays/Dropdown';
import { Sidebar } from '../../../components/overlays/Sidebar';
import { useResponsive } from '../../../hooks/useResponsive';
import { FilterSidebar, useThemeColors, useTranslation } from '../../../index';
import { useListBuilderContext } from '../core/ListBuilderProvider';

// Context to track grid layout mode (when true, controls should be rendered in grid)
const InlineControlsContext = createContext<boolean>(false);

export const useInlineControlsContext = () => useContext(InlineControlsContext);

export interface EmbeddedListLayoutProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	showFilters?: boolean;
	filterCollapsed?: boolean;
	onFilterToggle?: () => void;
	customFilterElement?: ReactNode;
	title?: string;
	headerAction?: ReactNode;
	/** Structured header actions config - used for mobile dropdown extraction */
	headerActionsConfig?: {
		primary?: Array<{
			label: string;
			onClick: () => void;
			icon?: ReactNode;
			hidden?: boolean;
		}>;
		secondary?: Array<{
			label: string;
			onClick: () => void;
			icon?: ReactNode;
			hidden?: boolean;
		}>;
	};
	/** Active filters element - shown on desktop, hidden on mobile (uses badge instead) */
	activeFiltersElement?: ReactNode;
	/** Count of active filters for mobile badge */
	activeFilterCount?: number;
	/** Saved search button and dropdown element (desktop only) */
	savedSearchElement?: ReactNode;
	/** Overflow menu items for hamburger menu (Configure List, Save as New List, etc.) */
	overflowMenuItems?: Array<{
		label: string;
		onClick: () => void;
		icon?: ReactNode;
		hidden?: boolean;
	}>;
}

/**
 * Layout component for list views that are embedded within detail views.
 * Unlike StandardListLayout, this doesn't include a ComponentHeader since
 * the parent detail view already provides one.
 */
export function EmbeddedListLayout({
	children,
	showFilters = true,
	filterCollapsed = false,
	onFilterToggle,
	customFilterElement,
	title,
	headerAction: _headerAction,
	headerActionsConfig,
	activeFiltersElement,
	activeFilterCount = 0,
	savedSearchElement,
	overflowMenuItems,
	className = '',
	...htmlProps
}: EmbeddedListLayoutProps) {
	// Filter out non-DOM props that might come from parent components
	const { showTabs, showConfigButton, ...cleanHtmlProps } = htmlProps as any;
	// Make context optional - component should work without ListBuilderProvider
	let state = null;
	let additionalContext = undefined;
	try {
		const context = useListBuilderContext();
		state = context.state;
		additionalContext = context.additionalContext;
	} catch (error) {
		// Component is used outside of ListBuilderProvider, which is fine
		state = null;
		additionalContext = undefined;
	}

	const { t } = useTranslation();
	const colors = useThemeColors();
	const { isMobile } = useResponsive();

	// Mobile filter sidebar state
	const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

	// Get enabled filters from configuration and validate them
	const activeFilters = state?.configuration?.filters?.activeFilters || [];
	let validatedFilters = validateActiveFilters(
		activeFilters,
		state?.configuration?.objects || [],
	);

	// Get hidden filters from state (set by role-based filter rules)
	const hiddenFilters = state?.hiddenFilters ?? {};

	// Remove filters hidden by role-based rules
	validatedFilters = validatedFilters.filter(
		(filterId) => hiddenFilters[filterId] !== true,
	);

	const hasFilters = showFilters && validatedFilters.length > 0;
	const hasCustomFilter = customFilterElement != null;
	const hasAnyFilters = hasFilters || hasCustomFilter;
	// Show filter column whenever filters exist (collapsed or expanded)
	const shouldShowFilterColumn = hasAnyFilters;
	const hasTitle = title != null;

	// State for reset button in sidebar
	const [sidebarFooter, setSidebarFooter] = useState<ReactNode>(null);

	// Callback to handle reset button from FilterGroup
	const handleRenderResetButton = useCallback((resetButton: ReactNode) => {
		setSidebarFooter(resetButton);
	}, []);

	// Determine grid configuration - memoized to avoid recalculating on every render
	// Width changes based on collapsed state (24px collapsed, 220px expanded)
	const leftColumnWidth = filterCollapsed ? '24px' : '220px';

	// Extract dropdown items from headerActionsConfig for mobile ellipsis menu
	const mobileDropdownItems = useMemo(() => {
		if (!isMobile || headerActionsConfig == null) {
			return [];
		}

		const items: Array<{ label: string; onClick: () => void }> = [];

		// Extract from primary actions
		headerActionsConfig.primary
			?.filter((a) => a.hidden !== true)
			.forEach((action) => {
				items.push({ label: action.label, onClick: action.onClick });
			});

		// Extract from secondary actions
		headerActionsConfig.secondary
			?.filter((a) => a.hidden !== true)
			.forEach((action) => {
				items.push({ label: action.label, onClick: action.onClick });
			});

		return items;
	}, [isMobile, headerActionsConfig]);

	const gridConfig = useMemo(() => {
		if (isMobile) {
			// Mobile: simple single-column layout
			// Controls are inside the title/header bar, not a separate row
			return {
				gridTemplateColumns: '1fr',
				gridTemplateRows: 'auto 1fr',
				gridTemplateAreas: '"header" "content"',
			};
		}

		if (hasTitle && shouldShowFilterColumn) {
			// Header spans full width (not affected by filter column width)
			// Filter column only affects the content row
			return {
				gridTemplateColumns: `${leftColumnWidth} 1fr`,
				gridTemplateRows: 'auto 1fr',
				gridTemplateAreas: '"header header" "filters content"',
			};
		} else if (hasTitle && !shouldShowFilterColumn) {
			// Header spans full width, content below
			return {
				gridTemplateColumns: '1fr',
				gridTemplateRows: 'auto 1fr',
				gridTemplateAreas: '"header" "content"',
			};
		} else if (!hasTitle && shouldShowFilterColumn) {
			// No header row, just filters and content
			return {
				gridTemplateColumns: `${leftColumnWidth} 1fr`,
				gridTemplateRows: '1fr',
				gridTemplateAreas: '"filters content"',
			};
		} else {
			// 1x1 grid: just content
			return {
				gridTemplateColumns: '1fr',
				gridTemplateRows: '1fr',
				gridTemplateAreas: '"content"',
			};
		}
	}, [hasTitle, shouldShowFilterColumn, isMobile, leftColumnWidth]);

	return (
		<InlineControlsContext.Provider value={true}>
			<Container
				variant="primary"
				padding="none"
				className={`flex-1 flex flex-col min-h-0 ${className}`}
				style={{ minHeight: '400px' }}
				data-testid="embedded-list-layout-container"
				{...cleanHtmlProps}
			>
				<div
					className={`flex-1 min-h-0 grid transition-[grid-template-columns] duration-200 ease-in-out ${isMobile ? 'gap-2' : 'p-4 gap-y-4'}`}
					style={gridConfig}
					data-testid="embedded-list-layout-inner"
				>
					{/* Header bar - full width, contains title + controls */}
					{/* On desktop: title on left, controls on right (in single row spanning full width) */}
					{/* On mobile: title + inline controls + action buttons */}
					{(hasTitle || isMobile) && (
						<div
							className={`px-4 rounded-lg flex items-center gap-4 ${colors.classes.bgSecondary}`}
							style={{
								gridArea: 'header',
								minHeight: '52px',
							}}
						>
							{/* Title - flex-shrink-0 to not compress */}
							{hasTitle && (
								<h3
									className={`text-lg font-semibold flex-shrink-0 ${colors.classes.textPrimary}`}
								>
									{t(title)}
								</h3>
							)}

							{/* Desktop: Saved search button (next to title) */}
							{!isMobile && savedSearchElement != null && (
								<div className="flex-shrink-0">{savedSearchElement}</div>
							)}

							{/* Desktop: Inline controls slot (tabs go here via portal) */}
							{!isMobile && (
								<div
									className="flex-1 flex items-center justify-end min-w-0"
									data-testid="embedded-list-inline-controls"
								>
									{/* Portal from ListBuilderView will render tabs + controls here */}
								</div>
							)}

							{/* Desktop: Overflow menu (hamburger) - flex-shrink-0 to prevent compression */}
							{!isMobile &&
								overflowMenuItems != null &&
								overflowMenuItems.filter((i) => i.hidden !== true).length >
									0 && (
									<div className="flex-shrink-0">
										<Dropdown
											trigger={
												<button
													className={`relative flex items-center justify-center w-10 h-10 rounded-lg ${colors.classes.bgTertiary} ${colors.classes.textSecondary}`}
													aria-label={t('More actions')}
													type="button"
												>
													<Icon icon={faEllipsisVertical} size={18} />
												</button>
											}
											items={overflowMenuItems.filter((i) => i.hidden !== true)}
											align="right"
										/>
									</div>
								)}

							{/* Mobile: Inline controls slot in header bar */}
							{isMobile && (
								<div
									className="flex-1 min-w-0 overflow-x-auto"
									data-testid="embedded-list-inline-controls"
								>
									{/* Portal from ListBuilderView will render inline controls here */}
								</div>
							)}

							{/* Mobile: Right side buttons */}
							{isMobile && (
								<div className="flex items-center gap-2 flex-shrink-0">
									{/* Mobile: ellipsis dropdown for actions */}
									{mobileDropdownItems.length > 0 && (
										<Dropdown
											trigger={
												<button
													className={`relative flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${colors.classes.bgTertiary} ${colors.classes.textSecondary}`}
													aria-label={t('More actions')}
													type="button"
												>
													<Icon icon={faEllipsisVertical} size={18} />
												</button>
											}
											items={mobileDropdownItems}
											align="right"
										/>
									)}
									{/* Mobile filter button with badge */}
									{hasAnyFilters && (
										<button
											onClick={() => {
												setMobileFilterOpen(true);
											}}
											className={`relative flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${colors.classes.bgTertiary} ${colors.classes.textSecondary}`}
											aria-label={t('Filters')}
											type="button"
										>
											<Icon icon={faFilter} size={18} />
											{activeFilterCount > 0 && (
												<span className="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center px-1.5 text-xs font-medium text-white bg-blue-600 rounded-full">
													{activeFilterCount}
												</span>
											)}
										</button>
									)}
								</div>
							)}
						</div>
					)}

					{/* Filter Sidebar - renders as collapsed rail or expanded panel (desktop only) */}
					{shouldShowFilterColumn && !isMobile && (
						<div
							className="flex flex-col min-h-0"
							style={{ gridArea: 'filters' }}
						>
							<FilterSidebar
								footer={filterCollapsed ? undefined : sidebarFooter}
								collapsed={filterCollapsed}
								onToggleCollapse={onFilterToggle}
							>
								{/* Custom filter element (like AddressSelector) */}
								{hasCustomFilter && (
									<div
										className={
											hasFilters
												? `mb-4 pb-4 border-b ${colors.classes.borderDefault}`
												: 'mb-4'
										}
									>
										{customFilterElement}
									</div>
								)}

								{/* Regular filters */}
								{hasFilters && (
									<FilterGroup
										filterTypes={validatedFilters}
										layout="sidebar"
										onRenderResetButton={handleRenderResetButton}
									/>
								)}
							</FilterSidebar>
						</div>
					)}

					{/* Main Content - connected to filter sidebar */}
					<div
						className={`flex flex-col min-h-0 min-w-0 ${shouldShowFilterColumn && !isMobile ? 'rounded-r-lg' : 'rounded-lg'}`}
						style={{ gridArea: 'content' }}
						data-testid="embedded-list-main-content"
					>
						{/* Active filters - desktop only, mobile uses badge on filter button */}
						{!isMobile && activeFiltersElement}
						{children}
					</div>
				</div>
			</Container>

			{/* Mobile Filter Sidebar */}
			{isMobile && hasAnyFilters && (
				<Sidebar
					isOpen={mobileFilterOpen}
					onClose={() => {
						setMobileFilterOpen(false);
					}}
					title={t('Filters')}
					width="sm"
					position="right"
				>
					<div className="p-4">
						{/* Custom filter element (like AddressSelector) */}
						{hasCustomFilter && (
							<div
								className={
									hasFilters
										? `mb-4 pb-4 border-b ${colors.classes.borderDefault}`
										: 'mb-4'
								}
							>
								{customFilterElement}
							</div>
						)}

						{/* Regular filters */}
						{hasFilters && (
							<FilterGroup filterTypes={validatedFilters} layout="sidebar" />
						)}
					</div>
				</Sidebar>
			)}
		</InlineControlsContext.Provider>
	);
}
